/**
 * Shared ClinicalHUD preferences for the product page, ClinicalHUD2, and the
 * global settings gear (visible on /clinical-hud and /clinical-hud-2): refresh
 * interval, display mode (v1 only), ChartMind DDx, Upgraded vs Legacy engine.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DEFAULT_CHARTMIND_DDX,
  DEFAULT_DDX_ENGINE,
  DEFAULT_EXPERT_ADVICE_ENABLED,
  DEFAULT_REFRESH_INTERVAL_SEC,
  DISPLAY_MODE_PHONE,
  REFRESH_INTERVAL_SECONDS,
  readStoredChartMindDdx,
  readStoredDdxEngine,
  readStoredDisplayMode,
  readStoredExpertAdviceEnabled,
  readStoredRefreshInterval,
  writeStoredChartMindDdx,
  writeStoredDdxEngine,
  writeStoredDisplayMode,
  writeStoredExpertAdviceEnabled,
  writeStoredRefreshInterval,
} from './clinicalHudRefreshSettings';

const ClinicalHudRefreshSettingsContext = createContext(null);

export function ClinicalHudRefreshSettingsProvider({ children }) {
  const [seconds, setSecondsState] = useState(readStoredRefreshInterval);
  const [displayMode, setDisplayModeState] = useState(readStoredDisplayMode);
  const [chartMindDdx, setChartMindDdxState] = useState(readStoredChartMindDdx);
  const [ddxEngine, setDdxEngineState] = useState(readStoredDdxEngine);
  const [expertAdviceEnabled, setExpertAdviceEnabledState] = useState(
    readStoredExpertAdviceEnabled,
  );

  const setSeconds = useCallback((next) => {
    setSecondsState(writeStoredRefreshInterval(next));
  }, []);

  const setDisplayMode = useCallback((next) => {
    setDisplayModeState(writeStoredDisplayMode(next));
  }, []);

  const setChartMindDdx = useCallback((next) => {
    setChartMindDdxState(writeStoredChartMindDdx(next));
  }, []);

  const setDdxEngine = useCallback((next) => {
    setDdxEngineState(writeStoredDdxEngine(next));
  }, []);

  const setExpertAdviceEnabled = useCallback((next) => {
    setExpertAdviceEnabledState(writeStoredExpertAdviceEnabled(next));
  }, []);

  const value = useMemo(
    () => ({
      seconds,
      setSeconds,
      options: REFRESH_INTERVAL_SECONDS,
      displayMode,
      setDisplayMode,
      chartMindDdx,
      setChartMindDdx,
      ddxEngine,
      setDdxEngine,
      expertAdviceEnabled,
      setExpertAdviceEnabled,
    }),
    [
      seconds,
      setSeconds,
      displayMode,
      setDisplayMode,
      chartMindDdx,
      setChartMindDdx,
      ddxEngine,
      setDdxEngine,
      expertAdviceEnabled,
      setExpertAdviceEnabled,
    ],
  );

  return (
    <ClinicalHudRefreshSettingsContext.Provider value={value}>
      {children}
    </ClinicalHudRefreshSettingsContext.Provider>
  );
}

ClinicalHudRefreshSettingsProvider.propTypes = {
  children: PropTypes.node,
};

/** Safe for chrome that may render outside the provider during tests. */
export function useClinicalHudRefreshSettingsOptional() {
  return useContext(ClinicalHudRefreshSettingsContext);
}

export function useClinicalHudRefreshSettings() {
  const ctx = useContext(ClinicalHudRefreshSettingsContext);
  if (!ctx) {
    return {
      seconds: DEFAULT_REFRESH_INTERVAL_SEC,
      setSeconds: () => {},
      options: REFRESH_INTERVAL_SECONDS,
      displayMode: DISPLAY_MODE_PHONE,
      setDisplayMode: () => {},
      chartMindDdx: DEFAULT_CHARTMIND_DDX,
      setChartMindDdx: () => {},
      ddxEngine: DEFAULT_DDX_ENGINE,
      setDdxEngine: () => {},
      expertAdviceEnabled: DEFAULT_EXPERT_ADVICE_ENABLED,
      setExpertAdviceEnabled: () => {},
    };
  }
  return ctx;
}
